"use client";

import { useState } from "react";
import { Image } from "@heroui/image";

import { cn } from "@/lib/utils";

interface PoweredByProps {
  /** The source URL or path for the company logo */
  logoSrc: string;
  /** Alt text for the logo image */
  logoAlt: string;
  /** Optional company name for additional context */
  companyName?: string;
  /** Custom className for styling overrides */
  className?: string;
  /** Size variant for the component */
  size?: "sm" | "md" | "lg";
  /** Whether to show the component (useful for conditional rendering) */
  show?: boolean;
}

export default function PoweredBy({
  logoSrc,
  logoAlt,
  companyName,
  className,
  size = "md",
  show = true,
}: PoweredByProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  if (!show) return null;

  const sizeClasses = {
    sm: {
      container: "gap-1",
      text: "text-xs",
      logo: "w-8 h-8",
      logoSize: { width: 32, height: 32 },
    },
    md: {
      container: "gap-1.5",
      text: "text-sm",
      logo: "w-12 h-12",
      logoSize: { width: 48, height: 48 },
    },
    lg: {
      container: "gap-2",
      text: "text-base",
      logo: "w-16 h-16",
      logoSize: { width: 64, height: 64 },
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      aria-label={`Powered by ${companyName || "company"}`}
      className={cn(
        // Fixed positioning in bottom-right corner
        "fixed bottom-4 right-4 z-50",
        // Responsive positioning adjustments
        "sm:bottom-6 sm:right-6",
        // Vertical layout with centered alignment
        "flex flex-col items-center select-none",
        // Spacing
        currentSize.container,
        className,
      )}
      role="contentinfo"
    >
      {/* Logo container with loading state */}
      <div
        className={cn(
          "relative flex items-center justify-center",
          currentSize.logo,
        )}
      >
        {/* Loading skeleton */}
        {imageLoading && !imageError && (
          <div
            aria-hidden="true"
            className={cn(
              "absolute inset-0 animate-pulse rounded-lg bg-gray-300/50 dark:bg-gray-600/50",
              currentSize.logo,
            )}
          />
        )}

        {/* Error fallback */}
        {imageError && (
          <div
            aria-label="Logo failed to load"
            className={cn(
              "flex items-center justify-center rounded-lg bg-gray-200/80 dark:bg-gray-700/80 backdrop-blur-sm",
              currentSize.logo,
            )}
          >
            <span className="text-sm text-gray-500 dark:text-gray-400">?</span>
          </div>
        )}

        {/* Company logo */}
        {!imageError && (
          <Image
            alt={logoAlt}
            decoding={"async"}
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
            className={cn(
              "object-contain transition-opacity duration-200 drop-shadow-lg",
              imageLoading ? "opacity-0" : "opacity-100",
            )}
            // height={currentSize.logoSize.height} // Ensure height matches
            // displayed size\\
            src={logoSrc || "/placeholder.svg"} // Use the provided logoSrc
            // width={currentSize.logoSize.width}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
            height={32}
            width={32}
            onLoad={() => setImageLoading(false)}
          />
        )}
      </div>

      {/* "Powered By" text with enhanced visibility */}
      <div className="flex flex-col items-center">
        <span
          className={cn(
            "font-medium text-gray-700 dark:text-gray-200",
            // Text shadow for better visibility on various backgrounds
            "drop-shadow-sm",
            // Backdrop blur for better readability
            "px-2 py-0.5 rounded-md bg-white/20 dark:bg-black/20 backdrop-blur-sm",
            currentSize.text,
          )}
        >
          Powered By
        </span>

        {/* Optional company name */}
        {companyName && (
          <span
            className={cn(
              "font-semibold text-gray-800 dark:text-gray-100 mt-0.5",
              "drop-shadow-sm",
              "px-2 py-0.5 rounded-md bg-white/20 dark:bg-black/20 backdrop-blur-sm",
              currentSize.text,
            )}
          >
            {companyName}
          </span>
        )}
      </div>
    </div>
  );
}
